import {
  ApiError,
  DomainExpenseType,
  DomainHousingType,
  DomainIncomeType,
} from "@porchpass/api-client";
import { toast } from "@porchpass/ui";
import { getFieldDisplayName } from "../constants/fieldDisplayNames";
import type { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type { useDgaApi } from "../hooks/useDgaApi";
import type {
  ApplicationFormData,
  BorrowerData,
} from "../types/applicationTypes";
import {
  PRICING_DOWN_PAYMENT_SOURCE_OTHER,
  shouldSkipAutosaveForIncompleteOther,
} from "@porchpass/ui";
import {
  validateAdditionalIncomeField,
  validateAddressField,
  validateAddressFields,
  validateDirtyHomeFields,
  validateDirtyPersonalInfoFields,
  validateExpenseField,
  validateFinancialAccountField,
} from "../utils/validationSchemas";
import {
  BORROWER_FIELDS,
  CONSENT_FIELDS,
  DEMOGRAPHICS_FIELDS,
} from "./saveFieldSets";
import {
  hasFilledFields,
  processPendingDeletes,
  saveDirtyAddresses,
  updateSufficientEmploymentHistory,
} from "./saveHelpers";
import { AutosaveFieldFailureError } from "./autosaveFieldFailure";
import { hasMinimumLandSaveData } from "./landMinimumSaveData";
import type { SaveHandler, SaveHandlerConfig } from "./useDebouncedAutoSave";

type DgaStore = ReturnType<typeof useApplicationFormStore>;
type DgaApi = ReturnType<typeof useDgaApi>;

/**
 * Dependencies the auto-save handlers need from the host component. Accessors
 * (`get*`) return the latest value on each call — preserving the original
 * "read the live ref" behaviour — while keeping this module free of React so it
 * is unit-testable with plain fakes.
 */
export interface AutoSaveDeps {
  getData: () => ApplicationFormData;
  getStore: () => DgaStore;
  getApi: () => DgaApi;
  /**
   * True during validation/poll flush (suppressHandlerToastsRef). Handlers must
   * set field errors but skip toasts. Today only duplicate contact uses this;
   * wire any new handler toasts through the same check.
   */
  getSuppressHandlerToasts: () => boolean;
}

/**
 * Build the auto-save `SaveHandlerConfig` (one handler per field domain). The
 * primary and co-borrower handlers come from a single factory parameterised by
 * `borrowerType`; the property/pricing/lenders handlers are domain-specific.
 */
export function createAutoSaveHandlers({
  getData,
  getStore,
  getApi,
  getSuppressHandlerToasts,
}: AutoSaveDeps): SaveHandlerConfig {
  // Single source of truth for borrower auto-save. The primary and co-borrower
  // handlers are identical apart from the field prefix, the borrower-id source,
  // the store update method, and the entity-delete key — all derived here from
  // `borrowerType`.
  const createBorrowerSaveHandler =
    (borrowerType: "primary" | "co"): SaveHandler<BorrowerData | undefined> =>
    async (borrower, dirtyFields) => {
      if (!borrower) return;
      const d = getData();
      const s = getStore();
      const currentApi = getApi();
      if (!d.applicationId) return;
      const borrowerId =
        borrowerType === "primary" ? d.primaryBorrowerId : d.coBorrowerId;
      if (!borrowerId) return;

      const prefix: "primaryBorrower" | "coBorrower" =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      // Use setBorrowerFieldWithoutDirty for server-response updates (e.g.
      // hasSufficientEmploymentHistory from the API response). Using
      // updatePrimaryBorrower/updateCoBorrower would mark the field dirty,
      // causing an extra save cycle that never converges.
      const updateBorrowerWithoutDirty = (
        updates: Parameters<typeof s.setBorrowerFieldWithoutDirty>[1],
      ) => s.setBorrowerFieldWithoutDirty(borrowerType, updates);
      const logLabel = borrowerType === "co" ? "co-borrower " : "";

      // Validate dirty personal info fields before saving (non-required errors only)
      const { validDirtyFields, fieldErrors } = validateDirtyPersonalInfoFields(
        borrower,
        dirtyFields,
      );
      Object.entries(fieldErrors).forEach(([field, message]) => {
        s.setFieldError(field, String(message));
      });
      if (validDirtyFields.size === 0) return;

      // Selective endpoint dispatch based on which fields are dirty
      const fieldsFor = (fieldSet: Set<string>) => {
        const matched = new Set<string>();
        for (const f of validDirtyFields) {
          if (fieldSet.has(f.replace(`${prefix}.`, ""))) matched.add(f);
        }
        return matched;
      };

      // If "Yes" is selected but no date yet, defer bankruptcy fields
      if (borrower.hasBankruptcy === true && !borrower.bankruptcyDate) {
        validDirtyFields.delete(`${prefix}.hasBankruptcy`);
        validDirtyFields.delete(`${prefix}.bankruptcyDate`);
      }

      const borrowerDirty = fieldsFor(BORROWER_FIELDS);
      const consentDirty = fieldsFor(CONSENT_FIELDS);
      const fieldSaveFailures = new Set<string>();

      if (borrowerDirty.size > 0) {
        const checkForDuplicateError = (err: Error): void => {
          const errorMsg = (err instanceof ApiError ? err.message : "") ?? "";
          const errorMsgLower = errorMsg.toLowerCase();

          // Check for hard error case (the email/phone duplicate within same app)
          if (errorMsgLower.includes("already in use")) {
            const msg = "Phone number or email is already in use";
            const affected: string[] = [];
            for (const f of borrowerDirty) {
              const field = f.replace(/^(primaryBorrower|coBorrower)\./, "");
              if (field === "phone" || field === "email") {
                s.setFieldError(f, msg);
                fieldSaveFailures.add(f);
                affected.push(getFieldDisplayName(f));
              }
            }
            if (affected.length > 0 && !getSuppressHandlerToasts()) {
              toast.error(
                `Couldn't save: ${[...new Set(affected)].join(", ")}. ${msg}.`,
              );
            } else if (affected.length === 0) {
              throw err;
            }
          } else {
            throw err;
          }
        };
        try {
          await currentApi.saveBorrower(borrower, borrowerId, borrowerDirty);
          // On success, clear duplicate warnings
          for (const f of borrowerDirty) {
            if (s.duplicateWarnings[f]) {
              s.setDuplicateWarning(f, null);
            }
          }
        } catch (err) {
          const errorMsg = (err instanceof ApiError ? err.message : "") ?? "";
          const errorMsgLower = errorMsg.toLowerCase();

          // Check for duplicate warning cases (show warning but keep field dirty)
          if (
            errorMsgLower.includes(
              "coborrower already exists as a coborrower on another app",
            ) ||
            errorMsgLower.includes(
              "coborrower already exists as a primary borrower on another app",
            )
          ) {
            try {
              await currentApi.saveBorrower(
                borrower,
                borrowerId,
                borrowerDirty,
                true,
              );
              // This is a warning case: set warning message
              const affectedFields: string[] = [];
              for (const f of borrowerDirty) {
                const field = f.replace(/^(primaryBorrower|coBorrower)\./, "");
                if (field === "email" || field === "phone") {
                  s.setDuplicateWarning(f, errorMsg);
                  affectedFields.push(f);
                }
              }
            } catch (err) {
              checkForDuplicateError(err as Error);
            }
          } else {
            checkForDuplicateError(err as Error);
          }
        }
      }

      const otherPuts: Promise<unknown>[] = [];
      if (consentDirty.size > 0)
        otherPuts.push(
          currentApi.saveBorrowerConsent(borrower, borrowerId, consentDirty),
        );
      if (fieldsFor(DEMOGRAPHICS_FIELDS).size > 0)
        otherPuts.push(currentApi.saveDemographics(borrower, borrowerId));
      if (otherPuts.length > 0) await Promise.all(otherPuts);

      // Save array entities only when their specific field is dirty
      const arrayDirty = (name: string) =>
        [...dirtyFields].some((f) => f.replace(`${prefix}.`, "") === name);

      // Get indices of dirty items within an array entity. Matches both
      // whole-array dirty ("<prefix>.expenses") and per-index dirty
      // ("<prefix>.expenses.0").
      const getDirtyIndices = (
        arrayName: string,
        arrayLength: number,
      ): number[] => {
        if (arrayDirty(arrayName)) {
          return Array.from({ length: arrayLength }, (_, i) => i);
        }
        const indices = new Set<number>();
        for (const f of dirtyFields) {
          const m = f.match(new RegExp(`^${prefix}\\.${arrayName}\\.(\\d+)`));
          if (m) indices.add(parseInt(m[1]));
        }
        return [...indices];
      };

      // Determine what address work is needed
      const fullAddressArrayDirty = arrayDirty("addresses");
      const dirtyAddressIndices = new Set<number>();
      for (const f of dirtyFields) {
        const m = f.match(new RegExp(`^${prefix}\\.addresses\\.(\\d+)`));
        if (m) dirtyAddressIndices.add(parseInt(m[1]));
      }
      const hasAddressWork =
        fullAddressArrayDirty ||
        dirtyAddressIndices.size > 0 ||
        (s.pendingEntityDeletes[`${borrowerType}.addresses`]?.length ?? 0) > 0;

      if (hasAddressWork) {
        // Process pending address deletes with per-item error handling
        await processPendingDeletes(s, `${borrowerType}.addresses`, (id) =>
          currentApi.deleteAddress(borrowerId, id),
        );

        // Determine which indices to save
        const indicesToSave = fullAddressArrayDirty
          ? borrower.addresses.map((_, i) => i)
          : [...dirtyAddressIndices];

        // Validate address fields and set/clear errors (scoped to dirty indices)
        for (const i of indicesToSave) {
          const addr = borrower.addresses[i];
          if (!addr) continue;
          const fieldsToValidate: ("zipCode" | "monthlyPayment")[] =
            addr.housingType === DomainHousingType.HousingNoPrimaryExpense
              ? ["zipCode"]
              : ["zipCode", "monthlyPayment"];
          for (const field of fieldsToValidate) {
            const value = addr[field]?.toString() ?? "";
            const errorKey = `${prefix}.addresses.${i}.${field}`;
            const error = validateAddressField(field, value);
            if (error) {
              s.setFieldError(errorKey, error);
            } else if (s.fieldErrors[errorKey]) {
              s.clearFieldError(errorKey);
            }
          }
        }

        // Create/update only the addresses that need saving
        await saveDirtyAddresses({
          borrowerId,
          addresses: borrower.addresses,
          indicesToSave,
          fieldPrefix: prefix,
          borrowerType,
          api: currentApi,
          store: s,
        });
      }
      // Each entity's deletes and saves are in separate try/catch blocks
      // so a failed delete doesn't block creates/updates, and one entity's
      // failure doesn't block other entities.

      // --- Employment deletes ---
      await processPendingDeletes(
        s,
        `${borrowerType}.employmentHistory`,
        (id) => currentApi.deleteEmployment(borrowerId, id),
      );
      // --- Employment creates/updates ---
      try {
        const dirtyEmpIndices = getDirtyIndices(
          "employmentHistory",
          borrower.employmentHistory.length,
        );
        for (const i of dirtyEmpIndices) {
          const emp = borrower.employmentHistory[i];
          if (!emp) continue;

          if (emp.serverId) {
            const resp = await currentApi.updateEmployment(
              borrowerId,
              emp.serverId,
              emp,
            );
            updateSufficientEmploymentHistory(resp, updateBorrowerWithoutDirty);
          } else if (hasFilledFields(emp)) {
            const resp = await currentApi.createEmployment(borrowerId, emp);
            const empData = resp.data as {
              employment_id?: string;
              has_sufficient_employment_history?: boolean;
            };
            if (empData.employment_id && emp.id) {
              s.setEntityServerId(
                borrowerType,
                "employmentHistory",
                emp.id,
                empData.employment_id,
              );
            }
            updateSufficientEmploymentHistory(resp, updateBorrowerWithoutDirty);
          }
        }

        // Validate employer address fields and set/clear errors
        for (const i of dirtyEmpIndices) {
          const emp = borrower.employmentHistory[i];
          if (!emp || !emp.employerAddress) continue;
          validateAddressFields(
            emp.employerAddress,
            `${prefix}.employmentHistory.${i}.employerAddress`,
            ["zipCode"],
            s.setFieldError.bind(s),
            (key: string) => s.fieldErrors[key] !== undefined,
            s.clearFieldError.bind(s),
          );
        }
      } catch (e) {
        console.error(`[autosave] ${logLabel}employment save failed:`, e);
      }

      // --- Financial account deletes ---
      await processPendingDeletes(
        s,
        `${borrowerType}.financialAccounts`,
        (id) => currentApi.deleteAsset(borrowerId, id),
      );
      // --- Financial account creates/updates ---
      try {
        const dirtyAcctIndices = getDirtyIndices(
          "financialAccounts",
          borrower.financialAccounts.length,
        );
        for (const i of dirtyAcctIndices) {
          const acct = borrower.financialAccounts[i];
          if (!acct) continue;
          // Validate
          if (acct.accountType) {
            for (const field of ["balance"] as const) {
              const value = acct[field]?.toString() ?? "";
              const errorKey = `${prefix}.financialAccounts.${i}.${field}`;
              const error = validateFinancialAccountField(field, value);
              if (error) {
                s.setFieldError(errorKey, error);
              } else if (s.fieldErrors[errorKey]) {
                s.clearFieldError(errorKey);
              }
            }
          }
          // Save
          if (acct.serverId) {
            await currentApi.updateAsset(borrowerId, acct.serverId, acct);
          } else if (hasFilledFields(acct)) {
            const resp = await currentApi.createAsset(borrowerId, acct);
            const acctData = resp.data as { asset_id?: string };
            if (acctData.asset_id && acct.id) {
              s.setEntityServerId(
                borrowerType,
                "financialAccounts",
                acct.id,
                acctData.asset_id,
              );
            }
          }
        }
      } catch (e) {
        console.error(
          `[autosave] ${logLabel}financial accounts save failed:`,
          e,
        );
      }

      // --- Additional income deletes ---
      await processPendingDeletes(s, `${borrowerType}.additionalIncome`, (id) =>
        currentApi.deleteIncome(borrowerId, id),
      );
      // --- Additional income creates/updates ---
      try {
        const dirtyIncIndices = getDirtyIndices(
          "additionalIncome",
          borrower.additionalIncome.length,
        );
        for (const i of dirtyIncIndices) {
          const inc = borrower.additionalIncome[i];
          if (!inc) continue;
          if (inc.type === DomainIncomeType.IncomeOther && !inc.typeOther)
            continue; // Incomplete "Other"
          // Validate
          if (inc.type) {
            for (const field of ["monthlyAmount"] as const) {
              const value = inc[field]?.toString() ?? "";
              const errorKey = `${prefix}.additionalIncome.${i}.${field}`;
              const error = validateAdditionalIncomeField(field, value);
              if (error) {
                s.setFieldError(errorKey, error);
              } else if (s.fieldErrors[errorKey]) {
                s.clearFieldError(errorKey);
              }
            }
          }
          // Save
          if (inc.serverId) {
            await currentApi.updateIncome(borrowerId, inc.serverId, inc);
          } else if (hasFilledFields(inc)) {
            const resp = await currentApi.createIncome(borrowerId, inc);
            const incData = resp.data as { income_id?: string };
            if (incData.income_id && inc.id) {
              s.setEntityServerId(
                borrowerType,
                "additionalIncome",
                inc.id,
                incData.income_id,
              );
            }
          }
        }
      } catch (e) {
        console.error(
          `[autosave] ${logLabel}additional income save failed:`,
          e,
        );
      }

      // --- Expense deletes ---
      await processPendingDeletes(s, `${borrowerType}.expenses`, (id) =>
        currentApi.deleteExpense(borrowerId, id),
      );
      // --- Expense creates/updates ---
      try {
        const dirtyExpIndices = getDirtyIndices(
          "expenses",
          borrower.expenses.length,
        );
        for (const i of dirtyExpIndices) {
          const exp = borrower.expenses[i];
          if (!exp) continue;
          if (
            exp.type === DomainExpenseType.ExpenseOtherSupport &&
            !exp.typeOther
          )
            continue; // Incomplete "Other"
          // Validate
          if (exp.type) {
            for (const field of ["monthlyAmount"] as const) {
              const value = exp[field]?.toString() ?? "";
              const errorKey = `${prefix}.expenses.${i}.${field}`;
              const error = validateExpenseField(field, value);
              if (error) {
                s.setFieldError(errorKey, error);
              } else if (s.fieldErrors[errorKey]) {
                s.clearFieldError(errorKey);
              }
            }
          }
          // Save
          if (exp.serverId) {
            await currentApi.updateExpense(borrowerId, exp.serverId, exp);
          } else if (hasFilledFields(exp)) {
            const resp = await currentApi.createExpense(borrowerId, exp);
            const expData = resp.data as { expense_id?: string };
            if (expData.expense_id && exp.id) {
              s.setEntityServerId(
                borrowerType,
                "expenses",
                exp.id,
                expData.expense_id,
              );
            }
          }
        }
      } catch (e) {
        console.error(`[autosave] ${logLabel}expenses save failed:`, e);
      }

      try {
        await currentApi.skipEmptyOptionalSections(borrower, borrowerId);
      } catch (e) {
        console.error(`[autosave] ${logLabel}optional section skip failed:`, e);
      }

      if (fieldSaveFailures.size > 0) {
        throw new AutosaveFieldFailureError(fieldSaveFailures);
      }
    };

  return {
    borrower: createBorrowerSaveHandler("primary"),
    coBorrower: createBorrowerSaveHandler("co"),
    home: async (home, dirtyFields) => {
      if (!home) return;
      const d = getData();
      const s = getStore();
      const currentApi = getApi();
      if (!d.applicationId) return;

      // Validate dirty home fields (format only, not required)
      const { validDirtyFields, fieldErrors } = validateDirtyHomeFields(
        home,
        dirtyFields,
      );
      Object.entries(fieldErrors).forEach(([field, message]) => {
        s.setFieldError(field, String(message));
      });
      if (validDirtyFields.size === 0) return;

      const propertyAddress = d.land?.propertyAddress;
      await currentApi.updateHome(
        d.applicationId,
        home,
        d.pricing,
        propertyAddress,
      );
    },
    land: async (land) => {
      if (!land) return;
      const d = getData();
      const currentApi = getApi();
      if (!d.applicationId) return;

      // Save land situation data once the backend HasMinimumData threshold is met
      // (situation-only selection returns 400 "no data provided").
      if (land.landSituation && hasMinimumLandSaveData(land)) {
        await currentApi.saveLand(d.applicationId, land);
      }

      // Property address is stored on the Home resource, not Land.
      // The store tracks dirty fields at top level ("land"), so we check
      // whether propertyAddress has any populated values to decide if we
      // should save it.
      const addr = land.propertyAddress;
      const hasPropertyAddressData =
        addr != null &&
        !!(
          addr.street ||
          addr.city ||
          addr.state ||
          addr.zipCode ||
          addr.county
        );
      if (hasPropertyAddressData) {
        const s = getStore();
        const addressFields = ["zipCode"] as const;
        for (const addressField of addressFields) {
          const value = String(addr[addressField] ?? "");
          const error = validateAddressField(addressField, value);
          const errorKey = `land.propertyAddress.${addressField}`;
          if (error) {
            s.setFieldError(errorKey, error);
          } else if (s.fieldErrors[errorKey]) {
            s.clearFieldError(errorKey);
          }
        }

        await currentApi.updateHome(
          d.applicationId,
          d.home,
          d.pricing,
          land.propertyAddress,
        );
      }

      // Refresh summary to pick up changes to land_purchase_or_payoff_amount
      // (e.g. when the payoff field is cleared because both toggles are "No")
      const summaryResp = await currentApi.getApplicationSummary(
        d.applicationId,
      );
      if (summaryResp.status === 200) {
        getStore().populateFromApi({
          pricing: {
            landPurchaseOrPayoffAmount:
              summaryResp.data.land_purchase_or_payoff_amount,
          },
        });
      }
    },
    pricing: async (pricing) => {
      if (!pricing) return;
      const d = getData();
      const currentApi = getApi();
      if (!d.applicationId) return;

      // No client-side format validation for pricing — AnimatedNumberInput
      // only emits valid numbers, and the component has no error display.

      // Most pricing fields are saved via the summary endpoint.
      // downPaymentSource / downPaymentSourceOther are saved via the Home
      // endpoint because the summary endpoint's backend struct does not
      // support these fields (swagger spec is out of sync with code).
      // Only call the Home endpoint when there's actually a source selected.
      const s = getStore();
      try {
        const summaryResp = await currentApi.updateApplicationSummary(
          d.applicationId,
          pricing,
        );
        const downPaymentSource = pricing.downPaymentSource;
        const shouldSaveDownPaymentSource =
          downPaymentSource != null &&
          downPaymentSource !== "" &&
          PRICING_DOWN_PAYMENT_SOURCE_OTHER.when(pricing) &&
          !shouldSkipAutosaveForIncompleteOther(
            downPaymentSource,
            pricing.downPaymentSourceOther ?? "",
            PRICING_DOWN_PAYMENT_SOURCE_OTHER,
            pricing,
          );
        if (shouldSaveDownPaymentSource) {
          const downPaymentSourceResp =
            await currentApi.updateDownPaymentSource(
              d.applicationId,
              downPaymentSource,
              pricing.downPaymentSourceOther ?? "",
            );
          if (downPaymentSourceResp.status !== 200) {
            console.warn(
              "[Pricing] down-payment-source save failed:",
              downPaymentSourceResp.status,
              downPaymentSourceResp.data,
            );
          }
        }
        if (summaryResp.status === 200) {
          // Only update loanAmount from the backend — preserve the user's
          // current input for all other pricing fields.  The `pricing` param
          // is a stale snapshot from when the debounce fired; spreading it
          // back would overwrite any changes the user made since then.
          s.populateFromApi({
            pricing: {
              loanAmount: summaryResp.data.loan_total_amount ?? 0,
              ...(summaryResp.data.land_purchase_or_payoff_amount != null && {
                landPurchaseOrPayoffAmount:
                  summaryResp.data.land_purchase_or_payoff_amount,
              }),
            },
          });
        }
      } finally {
        s.setLoanAmountLoading(false);
      }
    },
    lenders: async (lenders) => {
      if (!lenders || lenders.length === 0) return;
      const d = getData();
      const currentApi = getApi();
      if (!d.applicationId) return;
      await currentApi.saveApplicationLenders(d.applicationId, lenders);
    },
  };
}
